import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Label, Spinner, TextInput, Textarea } from "flowbite-react";
import { HiExternalLink } from "react-icons/hi";
import { auctionPropertiesThunks } from "../../app/store";
import PropertyMediaQueueGrid from "./shared/PropertyMediaQueueGrid";
import PropertyMediaSavedGrid from "./shared/PropertyMediaSavedGrid";
import PropertyMediaUploadPanel from "./shared/PropertyMediaUploadPanel";
import { usePropertyMediaManager } from "./shared/usePropertyMediaManager";
import { propertyGooglePlacesSnapshotsApi } from "../../api/propertyGooglePlacesSnapshotsApi";
import PropertyGooglePlacesInsights from "./PropertyGooglePlacesInsights";

const MEDIA_TYPE = "GOOGLE_MAPS_IMAGE";

function buildGoogleMapsUrl({ latitude, longitude, property_address }) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  if (hasCoords) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  const trimmed = (property_address || "").trim();
  if (!trimmed) return "https://www.google.com/maps/";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    trimmed,
  )}`;
}

export default function Step3GoogleMaps({ propertyId, property, onSave }) {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth?.user?.id);

  const [form, setForm] = useState({
    contexto: property?.contexto || "",
    relevo: property?.relevo || "",
  });
  const [labelBase, setLabelBase] = useState("gmaps");
  const [saving, setSaving] = useState(false);
  const [syncingGooglePlaces, setSyncingGooglePlaces] = useState(false);
  const hasCoordinates = Boolean(property?.latitude && property?.longitude);

  const {
    deletingId,
    onDeleteMedia,
    onFileInputChange,
    pasteBoxRef,
    queue,
    removeFromQueue,
    savedMedia,
    updateQueueLabel,
    uploadError,
    uploading,
    uploadQueue,
  } = usePropertyMediaManager({
    property,
    propertyId,
    onSave,
    mediaType: MEDIA_TYPE,
    labelBase,
    defaultClipboardBase: "GoogleMaps",
    userId,
  });

  useEffect(() => {
    setForm({
      contexto: property?.contexto || "",
      relevo: property?.relevo || "",
    });
  }, [propertyId, property?.contexto, property?.relevo]);

  async function saveFields() {
    if (!propertyId) return;
    setSaving(true);
    try {
      await dispatch(
        auctionPropertiesThunks.updateOne({
          id: propertyId,
          payload: {
            contexto: form.contexto?.trim() || null,
            relevo: form.relevo?.trim() || null,
          },
        }),
      ).unwrap();

      onSave?.();
    } finally {
      setSaving(false);
    }
  }

  async function handleSyncGooglePlaces() {
    if (!propertyId || !hasCoordinates) return;

    setSyncingGooglePlaces(true);
    try {
      await propertyGooglePlacesSnapshotsApi.syncGooglePlaces(propertyId);
      onSave?.();
    } catch (error) {
      console.error("Falha ao sincronizar Google POIs:", error);
    } finally {
      setSyncingGooglePlaces(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          className="gap-2"
          color="dark"
          onClick={() =>
            window.open(
          buildGoogleMapsUrl(property || {}),
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          Google Maps <HiExternalLink />
        </Button>

        <Button
          color="dark"
          onClick={handleSyncGooglePlaces}
          disabled={syncingGooglePlaces || !hasCoordinates}
        >
          {syncingGooglePlaces ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Syncing POIs
            </span>
          ) : (
            "Sync Google POIs"
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Contexto (Google Maps)</Label>
          <Textarea
            rows={4}
            value={form.contexto}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                contexto: event.target.value,
              }))
            }
            placeholder="Ex: bairro residencial, ruas asfaltadas, casas padrão médio, próximo a escola/mercado..."
          />
        </div>

        <div>
          <Label>Relevo (Google Maps)</Label>
          <TextInput
            value={form.relevo}
            onChange={(event) =>
              setForm((current) => ({ ...current, relevo: event.target.value }))
            }
            placeholder="Ex: flat / slight slope / hill"
          />
        </div>

        <div>
          <Label>Nome base das imagens</Label>
          <TextInput
            value={labelBase}
            onChange={(event) => setLabelBase(event.target.value)}
            placeholder="Ex: streetview, neighbors, aerial"
          />
        </div>
      </div>

      <PropertyMediaUploadPanel
        helperText="Clique nesta caixa e cole. As imagens vão aparecer para pré-visualização antes de enviar."
        onFileInputChange={onFileInputChange}
        onSave={saveFields}
        onUpload={uploadQueue}
        pasteBoxRef={pasteBoxRef}
        queueLength={queue.length}
        saveButtonLabel="Salvar Step 3"
        saving={saving}
        title="Cole prints aqui (Ctrl+V)"
        uploadError={uploadError}
        uploading={uploading}
      />

      <PropertyMediaQueueGrid
        items={queue}
        labelPlaceholder="Ex: streetview-front"
        onChangeLabel={updateQueueLabel}
        onRemove={removeFromQueue}
      />

      <PropertyMediaSavedGrid
        deletingId={deletingId}
        items={savedMedia}
        onDelete={onDeleteMedia}
        sectionTitle="Imagens já anexadas (Google Maps)"
        typeLabel="gmaps"
      />

      <PropertyGooglePlacesInsights
        snapshot={property?.googlePlacesSnapshots?.[0]}
      />
    </div>
  );
}
